import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { FC, useState } from "react";

import { FieldSeparator } from "@fronterahealth/frontera-ui-components/ui/field";
import { Skeleton } from "@fronterahealth/frontera-ui-components/ui/skeleton";

import {
  ReportSectionFieldsType,
  ReportSectionsType,
  useCreateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { ContentBlockCard } from "@components/SectionWidget/ContentBlockCard";
import { SupportedContentType, getFieldTypeByContent } from "@components/SectionWidget/SectionWidget";
import { notifyError } from "@components/notifications/notifications";
import { useReport } from "@providers/ReportProvider/useReport";

const CONTENT_TYPES: SupportedContentType[] = ["Text", "Table", "Image"];

// Renders the "Add Content" affordance for a section. Given an anchor `field` it sits
// between fields (hover-revealed) and inserts right after that field. Without one it's the
// leading control for a section with no fields yet — always visible, so a freshly-added
// section isn't a dead end with nothing to click.
export const ReportFieldAddContent: FC<{
  section: ReportSectionsType;
  field?: ReportSectionFieldsType;
}> = ({ field, section }) => {
  const { report, reportQuery } = useReport();
  const { mutate: mutateCreateField, isPending } = useCreateReportSectionFieldMutation({
    onSuccess: async (data) => {
      if (data.createReportSectionField?.status) {
        await reportQuery.refetch();
        setShowContentMenu(false);
      } else if (data.createReportSectionField?.status === false) {
        notifyError(data.createReportSectionField?.message ?? "Error adding content");
      }
    },
    onError: (error) => {
      console.error("Error while adding section field:", error);
      notifyError("Error Adding Section Field");
    },
  });
  const [showContentMenu, setShowContentMenu] = useState(false);

  const handleAddContent = async (type: SupportedContentType) => {
    const fieldType = getFieldTypeByContent(type);
    if (!fieldType) return;
    mutateCreateField({
      reportId: report.id,
      reportSectionId: section.id,
      fieldData: { title: "Enter Title", rankOrder: field ? field.rankOrder + 1 : 1, fieldType },
    });
  };

  return (
    <>
      {!showContentMenu && (
        <FieldSeparator
          onClick={() => setShowContentMenu(true)}
          className={clsx(
            "cursor-pointer transition-opacity",
            field ? "-mt-4 -mb-7 opacity-0 hover:opacity-100" : "mt-2",
          )}
        >
          Add Content
        </FieldSeparator>
      )}
      {showContentMenu && !isPending && (
        <div className="flex justify-center rounded-md w-full bg-background-secondary">
          <div className="flex flex-row my-4 grow justify-center">
            {CONTENT_TYPES.map((typeName) => (
              <ContentBlockCard key={typeName} typeName={typeName} onClick={() => handleAddContent(typeName)} />
            ))}
          </div>
          <XMarkIcon
            onClick={() => setShowContentMenu(false)}
            className="h-6 w-6 cursor-pointer bg-transparent m-2 text-text-primary"
          />
        </div>
      )}
      {isPending && (
        <div className="flex w-full flex-col mt-2">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      )}
    </>
  );
};
